/**
 * Statement Builder bottom-half components.
 *
 * Three focused panels for the guided writing workspace below the audit:
 *   <StatementGoalCard>         — condition-specific "what a strong statement covers"
 *   <ConditionPromptCard>       — extends PromptCard with whyItMatters + Quick-Answer chips
 *   <GapsPanelV2>               — Required / Recommended buckets with Fix-now buttons
 *   <DraftActionButtons>        — Generate / Concise / Detailed / Human / Weak-wording-check
 *   <PreviewEmptyStateV2>       — improved empty-state copy
 *
 * These were lifted out of StatementsView so the view file stays readable.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Target, Lightbulb, Warning, WarningCircle, ArrowRight, CheckCircle, Sparkle,
  ListChecks, Robot, ArrowsInLineHorizontal, ArrowsOutLineHorizontal, ChatCircle, Eye,
  CaretRight, CaretDown, Copy, Link as LinkIcon,
} from "@phosphor-icons/react";
import {
  detectVaguePhrases, detectToneRisks, STATUS_META, statusToneColor,
} from "@/lib/statementAudit";
import { classifyGaps, dimensionToPromptId, applyChipInsert } from "@/lib/conditionPrompts";
import {
  VagueFlagInline, ToneRiskInline, EvidenceLinkButton,
} from "@/components/StatementBuilderPanels";
import { getPromptCoaching, wordCount } from "@/lib/claimUtils";

// ---------------------------------------------------------------------------
// StatementGoalCard — condition-specific reminder above the prompts
// ---------------------------------------------------------------------------

export function StatementGoalCard({ goal, category, side }) {
  if (!goal) return null;
  return (
    <div
      className="panel"
      data-testid="stmt-goal-card"
      style={{
        borderRadius: 14,
        background: "linear-gradient(135deg, rgba(31,58,107,0.06), rgba(31,58,107,0.02))",
        border: "1px solid rgba(31,58,107,0.22)",
      }}
    >
      <div className="panel-body" style={{ padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <Target size={16} weight="duotone" color="#1F3A6B" />
          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.10em", color: "#1F3A6B" }}>
            STATEMENT GOAL
          </span>
          {category && category !== "default" && (
            <span
              className="mono"
              data-testid="stmt-goal-category"
              style={{
                marginLeft: "auto",
                fontSize: 9.5,
                padding: "2px 8px",
                background: "rgba(31,58,107,0.10)",
                border: "1px solid rgba(31,58,107,0.30)",
                color: "#1F3A6B",
                borderRadius: 999,
                fontWeight: 700,
                letterSpacing: "0.06em",
              }}
            >
              {category.replace(/-/g, " ").toUpperCase()}{side ? ` · ${side.toUpperCase()}` : ""}
            </span>
          )}
        </div>
        <div
          data-testid="stmt-goal-text"
          style={{ fontSize: 13.5, lineHeight: 1.6, color: "var(--text-primary)" }}
        >
          {goal}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConditionPromptCard — extended prompt card with whyItMatters + chips
// ---------------------------------------------------------------------------

export const ConditionPromptCard = React.forwardRef(function ConditionPromptCard({
  prompt, type, value, onChange, isOpen, onToggle, autoFocus,
  evidenceLinks, onEvidenceChange, sources,
}, ref) {
  // Use the prompt's own whyItMatters if provided; otherwise fall back to
  // the legacy coaching tip in claimUtils.
  const fallback = getPromptCoaching(type, prompt.id);
  const why = prompt.whyItMatters || fallback?.tip;
  const example = fallback?.example;
  const [showExample, setShowExample] = useState(false);
  const filled = (value || "").trim().length > 0;
  const wc = wordCount(value);
  const vague = useMemo(() => detectVaguePhrases(value || ""), [value]);
  const risks = useMemo(() => detectToneRisks(value || ""), [value]);
  const textareaRef = useRef(null);

  function handleChipClick(chip) {
    onChange(applyChipInsert(value, chip.insert));
    // Bring the textarea back into focus so the vet can keep editing.
    requestAnimationFrame(() => { textareaRef.current?.focus(); });
  }

  return (
    <div
      ref={ref}
      className={`stmt-card ${isOpen ? "stmt-card--open" : ""} ${filled ? "stmt-card--filled" : ""}`}
      data-testid={`prompt-${prompt.id}`}
      id={`prompt-card-${prompt.id}`}
    >
      <button className="stmt-card-head" onClick={onToggle} data-testid={`prompt-toggle-${prompt.id}`}>
        <span className="stmt-card-status" aria-hidden="true">
          {filled ? <CheckCircle size={18} weight="fill" /> : <span className="stmt-card-bullet" />}
        </span>
        <span className="stmt-card-titlebar">
          <span className="stmt-card-title">
            {prompt.label}
            {prompt.optional && <span className="stmt-card-optional"> (optional)</span>}
          </span>
          {filled && <span className="stmt-card-wc mono">{wc} word{wc === 1 ? "" : "s"}</span>}
          {(vague.length > 0 || risks.length > 0) && (
            <span
              data-testid={`prompt-warn-dot-${prompt.id}`}
              title={`${vague.length} vague + ${risks.length} tone flag${(vague.length + risks.length) === 1 ? "" : "s"}`}
              style={{
                width: 8, height: 8, borderRadius: 999,
                background: risks.some((r) => r.severity === "warn") ? "#dc2626" : "#d97706",
                marginLeft: 6,
              }}
            />
          )}
        </span>
        {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
      </button>

      {isOpen && (
        <div className="stmt-card-body">
          {why && (
            <div className="stmt-tip" data-testid={`prompt-why-${prompt.id}`}>
              <Lightbulb size={13} weight="duotone" />
              <span><strong>Why this matters:</strong> {why}</span>
            </div>
          )}
          <textarea
            ref={textareaRef}
            className="field-input field-textarea stmt-textarea"
            placeholder={prompt.placeholder}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            autoFocus={autoFocus}
            data-testid={`prompt-input-${prompt.id}`}
            rows={5}
          />

          {/* Quick-Answer Builder chips */}
          {prompt.chips && prompt.chips.length > 0 && (
            <div
              data-testid={`prompt-chips-${prompt.id}`}
              style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}
            >
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "var(--text-secondary)", alignSelf: "center", marginRight: 4 }}>
                QUICK ANSWERS
              </span>
              {prompt.chips.map((chip, i) => (
                <button
                  key={`${prompt.id}-chip-${i}`}
                  type="button"
                  data-testid={`prompt-chip-${prompt.id}-${i}`}
                  onClick={() => handleChipClick(chip)}
                  title={`Insert: "${chip.insert}"`}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "4px 10px",
                    background: "var(--surface-2, #F1F5F9)",
                    border: "1px solid var(--border)",
                    borderRadius: 999,
                    color: "var(--text-primary)",
                    cursor: "pointer",
                    transition: "background 140ms ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(31,58,107,0.08)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "var(--surface-2, #F1F5F9)"; }}
                >
                  + {chip.label}
                </button>
              ))}
            </div>
          )}

          <VagueFlagInline flags={vague} testId={`prompt-vague-${prompt.id}`} />
          <ToneRiskInline risks={risks} testId={`prompt-tone-${prompt.id}`} />

          <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-start" }}>
            <EvidenceLinkButton
              promptId={prompt.id}
              linked={evidenceLinks || []}
              sources={sources}
              onChange={onEvidenceChange}
            />
            {example && (
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => setShowExample(!showExample)}
                data-testid={`prompt-example-toggle-${prompt.id}`}
              >
                {showExample ? "Hide example" : "Show example answer"}
              </button>
            )}
          </div>

          {example && showExample && (
            <div className="stmt-example" data-testid={`prompt-example-${prompt.id}`} style={{ marginTop: 10 }}>
              <div className="stmt-example-label">EXAMPLE</div>
              <div className="stmt-example-text">{example}</div>
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => onChange((value ? value.trim() + "\n\n" : "") + example)}
                data-testid={`prompt-insert-example-${prompt.id}`}
              >
                <Copy size={12} weight="bold" /> Insert as starting point
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// GapsPanelV2 — Required / Recommended buckets with Fix-now buttons
// ---------------------------------------------------------------------------

export function GapsPanelV2({ dimensions, prompts, onJumpTo }) {
  const { required, recommended } = useMemo(() => classifyGaps(dimensions || []), [dimensions]);
  if (required.length === 0 && recommended.length === 0) {
    return (
      <div className="stmt-gaps" data-testid="stmt-gaps-empty">
        <div className="stmt-gaps-title" style={{ color: "#047857" }}>
          <CheckCircle size={14} weight="fill" /> NO GAPS — STATEMENT IS COMPLETE
        </div>
        <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
          Every audit dimension is Strong. You can export or share with confidence.
        </div>
      </div>
    );
  }
  return (
    <div data-testid="stmt-gaps-v2" style={{ display: "grid", gap: 10 }}>
      <GapBucket
        title="Required for strong statement"
        tone="red"
        icon={WarningCircle}
        gaps={required}
        prompts={prompts}
        onJumpTo={onJumpTo}
        testIdPrefix="stmt-gap-required"
      />
      <GapBucket
        title="Recommended strengtheners"
        tone="amber"
        icon={Warning}
        gaps={recommended}
        prompts={prompts}
        onJumpTo={onJumpTo}
        testIdPrefix="stmt-gap-recommended"
      />
    </div>
  );
}

function GapBucket({ title, tone, icon: Icon, gaps, prompts, onJumpTo, testIdPrefix }) {
  if (!gaps || gaps.length === 0) return null;
  const palette = statusToneColor(tone);
  return (
    <div
      data-testid={testIdPrefix}
      style={{
        padding: "10px 12px",
        borderRadius: 12,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Icon size={14} weight="fill" color={palette.text} />
        <span style={{ fontSize: 10.5, fontWeight: 800, color: palette.text, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          {title}
        </span>
        <span className="mono" style={{ marginLeft: "auto", fontSize: 10, color: palette.text, opacity: 0.75 }}>
          {gaps.length} ITEM{gaps.length === 1 ? "" : "S"}
        </span>
      </div>
      <div style={{ display: "grid", gap: 6 }}>
        {gaps.map((g) => {
          const meta = STATUS_META[g.status] || STATUS_META["needs-review"];
          const targetPromptId = dimensionToPromptId(g.id, prompts || []);
          return (
            <div
              key={`${testIdPrefix}-${g.id}`}
              data-testid={`${testIdPrefix}-${g.id}`}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                padding: "8px 10px",
                background: "rgba(255,255,255,0.6)",
                border: "1px solid var(--border)",
                borderRadius: 8,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-primary)" }}>
                  {g.label} <span className="mono" style={{ fontSize: 10, color: palette.text, fontWeight: 700, letterSpacing: "0.06em", marginLeft: 4 }}>· {meta.label.toUpperCase()}</span>
                </div>
                {g.suggestion && (
                  <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginTop: 2, lineHeight: 1.5 }}>
                    {g.suggestion}
                  </div>
                )}
              </div>
              {targetPromptId && (
                <button
                  type="button"
                  className="btn btn--outline btn--sm"
                  onClick={() => onJumpTo(targetPromptId)}
                  data-testid={`${testIdPrefix}-fixnow-${g.id}`}
                  style={{ flexShrink: 0 }}
                >
                  Fix now <ArrowRight size={11} weight="bold" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DraftActionButtons — Generate / Concise / Detailed / Human / Weak-wording
// ---------------------------------------------------------------------------

const ACTIONS = [
  { id: "generate",     label: "Generate Draft",       icon: Robot,                     tone: "accent" },
  { id: "concise",      label: "Make More Concise",    icon: ArrowsInLineHorizontal,    tone: "outline" },
  { id: "detailed",     label: "Make More Detailed",   icon: ArrowsOutLineHorizontal,   tone: "outline" },
  { id: "human",        label: "Make More Human",      icon: ChatCircle,                tone: "outline" },
  { id: "weak-wording", label: "Check for Weak Wording", icon: Eye,                     tone: "outline" },
];

export function DraftActionButtons({
  hasContent, runningAction, lastOutput, error, onRun, onClear, onApply,
}) {
  if (!hasContent && !runningAction) return null;
  return (
    <div className="panel" data-testid="stmt-draft-actions" style={{ borderRadius: 14 }}>
      <div className="panel-header">
        <Sparkle size={16} weight="duotone" />
        <span>DRAFT ACTIONS</span>
        {runningAction && (
          <span className="mono header-count" data-testid="stmt-draft-running">
            {ACTIONS.find((a) => a.id === runningAction)?.label.toUpperCase() || runningAction.toUpperCase()}…
          </span>
        )}
      </div>
      <div className="panel-body" style={{ padding: 14 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {ACTIONS.map((a) => {
            const Icon = a.icon;
            const isRunning = runningAction === a.id;
            const cls = a.tone === "accent" ? "btn btn--accent btn--sm" : "btn btn--outline btn--sm";
            return (
              <button
                key={a.id}
                type="button"
                className={cls}
                disabled={!!runningAction}
                onClick={() => onRun(a.id)}
                data-testid={`stmt-draft-action-${a.id}`}
              >
                <Icon size={12} weight="bold" />
                {isRunning ? `${a.label}…` : a.label}
              </button>
            );
          })}
        </div>
        {error && (
          <div
            data-testid="stmt-draft-error"
            style={{
              marginTop: 10, padding: "8px 10px", borderRadius: 8,
              background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.30)",
              fontSize: 12, color: "#b91c1c",
            }}
          >
            {error}
          </div>
        )}
        {lastOutput && (
          <div
            data-testid="stmt-draft-output"
            style={{
              marginTop: 12, padding: 12, borderRadius: 10,
              background: "var(--surface-2, #F1F5F9)", border: "1px solid var(--border)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <Sparkle size={12} weight="fill" color="#1F3A6B" />
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "#1F3A6B" }}>
                {ACTIONS.find((a) => a.id === lastOutput.action)?.label.toUpperCase() || "OUTPUT"}
              </span>
              <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                {/* Apply to draft — only for rewrite actions (not weak-wording critique).
                    Opens a per-section diff modal so the vet sees what would change
                    before any override is set. Structured answers stay untouched. */}
                {lastOutput.action !== "weak-wording" && onApply && (
                  <button
                    type="button"
                    className="btn btn--accent btn--sm"
                    onClick={() => onApply(lastOutput.output, lastOutput.action)}
                    data-testid="stmt-draft-apply"
                    title="Open a section-by-section diff to choose what to apply"
                  >
                    <ArrowRight size={11} weight="bold" /> APPLY TO DRAFT
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn--outline btn--sm"
                  onClick={() => {
                    if (navigator.clipboard?.writeText) {
                      navigator.clipboard.writeText(lastOutput.output).then(() => {});
                    }
                  }}
                  data-testid="stmt-draft-copy"
                >
                  <Copy size={11} weight="bold" /> COPY
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={onClear}
                  data-testid="stmt-draft-clear"
                >
                  Dismiss
                </button>
              </span>
            </div>
            <pre
              data-testid="stmt-draft-output-text"
              style={{
                fontFamily: "var(--font-sans, -apple-system)",
                fontSize: 12.5,
                lineHeight: 1.6,
                color: "var(--text-primary)",
                whiteSpace: "pre-wrap",
                margin: 0,
                maxHeight: 380,
                overflowY: "auto",
              }}
            >
              {lastOutput.output}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PreviewEmptyStateV2 — improved empty-state guidance copy
// ---------------------------------------------------------------------------

export function PreviewEmptyStateV2() {
  return (
    <div className="stmt-preview-empty" data-testid="stmt-preview-empty" style={{ padding: 24 }}>
      <ListChecks size={28} weight="duotone" />
      <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "var(--text-primary)", marginTop: 12 }}>
        Your statement will build here as you answer each section. Start with <strong>onset</strong>, then explain <strong>service connection</strong>, <strong>symptoms</strong>, <strong>continuity</strong>, <strong>current impact</strong>, and <strong>treatment</strong>.
      </div>
    </div>
  );
}

// Hook used by the parent view to scroll a target prompt card into view + open it.
export function useScrollToPrompt() {
  return function scrollAndOpen(promptId, openSetter) {
    if (!promptId) return;
    openSetter(promptId);
    requestAnimationFrame(() => {
      const el = document.getElementById(`prompt-card-${promptId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };
}

// Unused-icon stub so linter doesn't complain (LinkIcon imported for type clarity).
export const __unused = { LinkIcon };
